'use client';
import styles from './FeedbackForm.module.scss';
import { useTranslations } from 'next-intl';
import { Modal } from '@/shared/ui/Modal';
import { useFeedbackModal } from './hooks/useFeedbackModal';
import { useEffect } from 'react';

interface FeedbackFormProps {
  isOpen: boolean;
  onClose: () => void;
}

export function FeedbackForm({ isOpen, onClose }: FeedbackFormProps) {
  const t = useTranslations('ui.feedbackForm');
  const {
    formData,
    isLoading,
    error,
    success,
    submitError,
    fieldErrors,
    handleInputChange,
    handleSubmit,
    handleReset,
  } = useFeedbackModal();

  const handleClose = () => {
    handleReset();
    onClose();
  };

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        handleClose();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const footerContent = (
    <footer className={styles.buttonContainer}>
      <button
        className={`${styles.button} ${styles.button_sent}`}
        onClick={handleSubmit}
        disabled={isLoading || success}
      >
        <span className={styles.button_text}>
          {isLoading ? (
            <>
              <span className={styles.spinner} />
              {t('sending')}
            </>
          ) : (
            t('send')
          )}
        </span>
      </button>
      <button
        className={`${styles.button} ${styles.button_close}`}
        onClick={handleClose}
      >
        <span className={styles.button_text}>{t('close')}</span>
      </button>
    </footer>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      label={t('modal')}
      isCloseIcon={true}
      padding="32px"
      footer={footerContent}
    >
      <div className={styles.formWrapper}>
        <div className={styles.formHeader}>
          <p className={styles.formSubtitle}>{t('subtitle')}</p>
        </div>

        <div className={styles.form}>
          <div className={styles.form_row}>
            <div className={styles.form_field}>
              <label className={styles.label}>
                {t('name')}
                <span className={styles.required}>*</span>
              </label>
              <div className={`${styles.inputWrapper} ${fieldErrors?.full_name ? styles.inputWrapper_error : ''}`}>
                <input
                  className={styles.input}
                  name="full_name"
                  placeholder={t('namePlaceholder')}
                  value={formData.full_name}
                  onChange={handleInputChange('full_name')}
                  autoComplete="name"
                />
                {fieldErrors?.full_name && (
                  <span className={styles.fieldError}>{fieldErrors.full_name}</span>
                )}
              </div>
            </div>

            <div className={styles.form_field}>
              <label className={styles.label}>
                {t('email')}
                <span className={styles.required}>*</span>
              </label>
              <div className={`${styles.inputWrapper} ${fieldErrors?.email ? styles.inputWrapper_error : ''}`}>
                <input
                  className={styles.input}
                  name="email"
                  type="email"
                  placeholder={t('emailPlaceholder')}
                  value={formData.email}
                  onChange={handleInputChange('email')}
                  autoComplete="email"
                />
                {fieldErrors?.email && (
                  <span className={styles.fieldError}>{fieldErrors.email}</span>
                )}
              </div>
            </div>
          </div>
          <div className={styles.form_row}>
            <div className={styles.form_field}>
              <label className={styles.label}>
                {t('company')}
              </label>
              <div className={`${styles.inputWrapper} ${fieldErrors?.company_name ? styles.inputWrapper_error : ''}`}>
                <input
                  className={styles.input}
                  name="company_name"
                  placeholder={t('companyPlaceholder')}
                  value={formData.company_name}
                  onChange={handleInputChange('company_name')}
                  autoComplete="organization"
                />
                {fieldErrors?.company_name && (
                  <span className={styles.fieldError}>{fieldErrors.company_name}</span>
                )}
              </div>
            </div>

            <div className={styles.form_field}>
              <label className={styles.label}>
                {t('phone')}
              </label>
              <div className={styles.inputWrapper}>
                <input
                  className={styles.input}
                  name="phone_number"
                  type="tel"
                  placeholder={t('phonePlaceholder')}
                  value={formData.phone_number}
                  onChange={handleInputChange('phone_number')}
                  autoComplete="tel"
                />
              </div>
            </div>
          </div>
          <div className={styles.form_field}>
            <label className={styles.label}>
              {t('specialization')}
              <span className={styles.required}>{t('optional')}</span>
              <span className={styles.charCount}>
                {formData.company_specialization.length}/500
              </span>
            </label>
            <div className={`${styles.inputWrapper} ${fieldErrors?.company_specialization ? styles.inputWrapper_error : ''}`}>
              <textarea
                className={`${styles.input} ${styles.textarea}`}
                name="company_specialization"
                placeholder={t('specializationPlaceholder')}
                value={formData.company_specialization}
                onChange={handleInputChange('company_specialization')}
                rows={3}
              />
              {fieldErrors?.company_specialization && (
                <span className={styles.fieldError}>{fieldErrors.company_specialization}</span>
              )}
            </div>
          </div>
          {/* <div className={styles.form_field}>
            <label className={styles.label}>
              {t('details')}
              <span className={styles.optional}>{t('optional')}</span>
              <span className={styles.charCount}>
                {formData.details.length}/500
              </span>
            </label>
            <div className={styles.inputWrapper}>
              <textarea
                className={`${styles.input} ${styles.textarea}`}
                name="details"
                placeholder={t('detailsPlaceholder')}
                value={formData.details}
                onChange={handleInputChange('details')}
                rows={3}
              />
            </div>
          </div> */}
          {submitError && (
            <div className={`${styles.message} ${styles.message_error}`}>
              <span className={styles.message_icon}>✕</span>
              {submitError}
            </div>
          )}
          {error && (
            <div className={`${styles.message} ${styles.message_error}`}>
              <span className={styles.message_icon}>✕</span>
              {t('errorMessage')}
            </div>
          )}
          {success && (
            <div className={`${styles.message} ${styles.message_success}`}>
              <span className={styles.message_icon}>✓</span>
              {t('successMessage')}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
